import React, { useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import {
  Col,
  Row,
  Tooltip,
  Button,
  Flex,
  Skeleton,
  Typography,
  Space,
  Dropdown,
  Modal,
  message,
  Popover,
  Select,
  Tag,
} from "antd";
import {
  ContainerOutlined,
  FilePdfFilled,
  MoreOutlined,
  StarFilled,
  StopOutlined,
  TranslationOutlined,
  UserOutlined,
} from "@ant-design/icons";
import TagsComponent from "../Tags/tags";
import GroupsComponent from "../Groups/groups";
import PhoneDisplay from "../../../common/PhoneDisplay";
import { isWebchatMobile } from "../../../../utils/format";
import { GlobalOutlined } from "@ant-design/icons";
import { useStore } from "../../inbox-store";
import { useFeature } from "../../../common/FeatureGate";
import { getUserLanguage, langName, languageOptions } from "../../../../utils/languages";
import { getAvailableChannels } from "../../../../utils/channels";
import { useWhatsappWindow } from "../../../../hooks/useWhatsappWindow";
import ChannelIcon from "../../../common/ChannelIcon";
import { WhatsAppWindowChip } from "../WhatsAppWindowChip";
import { effectiveLastInboundAt, latestInboundThread } from "../../utils/whatsappChannel";

// ADR-025: one chip per channel the contact can be reached on. SMS shows the
// number; WhatsApp shows the live 24h-window state and jumps the composer to it.
const ContactChannelChips = ({ contact }) => {
  const threads = useStore((state) => state.threads);
  const setComposerChannel = useStore((state) => state.setComposerChannel);
  const channels = getAvailableChannels(contact);
  const sms = channels.find((c) => c.type === 'sms');
  const wa = channels.find((c) => c.type === 'whatsapp');
  // Only inbound threads pushed after this model loaded can move the window.
  const modelLoadedAt = useMemo(() => Date.now(), [contact]);
  const latestWaInbound = latestInboundThread(threads, 'whatsapp', modelLoadedAt);
  const waLastInbound = effectiveLastInboundAt(wa, threads, modelLoadedAt);
  const waState = useWhatsappWindow(waLastInbound, [latestWaInbound?.id]);

  return (
    <>
      {sms && sms.address && (
        <Tag
          data-testid="contact-channel-sms"
          icon={<ChannelIcon channel="sms" size={12} />}
          style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, lineHeight: '20px' }}
        >
          <PhoneDisplay value={sms.address} />
        </Tag>
      )}
      {wa && (
        <WhatsAppWindowChip
          state={waState}
          hasConversation={!!wa.conversationId}
          onClick={() => setComposerChannel('whatsapp')}
        />
      )}
    </>
  );
};

// ADR-035: the contact's language drives inbound translation + the composer's
// translate switch. Detected once from their first message, overridable here.
const ContactLanguagePill = ({ contact }) => {
  const setContactLanguage = useStore((state) => state.setContactLanguage);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const viewerLang = getUserLanguage();
  const lang = contact.language || null;
  const differs = !!lang && lang !== viewerLang;

  const onChange = async (value) => {
    setSaving(true);
    try {
      await setContactLanguage(value || null);
    } finally {
      setSaving(false);
      setOpen(false);
    }
  };

  const content = (
    <div style={{ width: 240 }}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
        Language this contact writes in. Messages are translated to and from it.
      </Text>
      <Select
        autoFocus
        showSearch
        allowClear
        size="small"
        style={{ width: '100%' }}
        placeholder="Select a language"
        optionFilterProp="label"
        value={lang || undefined}
        options={languageOptions(lang)}
        loading={saving}
        onChange={onChange}
        onClear={() => onChange(null)}
      />
      {contact.language_source === 'detected' && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
          Detected from their messages. Choosing a language here overrides detection.
        </Text>
      )}
    </div>
  );

  return (
    <Popover trigger="click" open={open} onOpenChange={setOpen} content={content} placement="bottomLeft">
      <Tooltip title={lang ? 'Contact language — click to change' : 'Set the language this contact writes in'} placement="top">
        <Tag
          icon={<TranslationOutlined />}
          color={differs ? 'blue' : undefined}
          style={{ cursor: 'pointer', margin: 0, fontSize: 12, lineHeight: '20px' }}
        >
          {lang ? langName(lang) : 'Set language'}
          {lang && contact.language_source === 'detected' ? <span style={{ opacity: 0.7 }}> · detected</span> : null}
        </Tag>
      </Tooltip>
    </Popover>
  );
};

const { Text, Title } = Typography;

const menuItems = [
  {
    label: "Delete Contact",
    key: "1",
    icon: <UserOutlined />,
  },
];

const ActiveContactHeader = ({ history, onDeleteContact }) => {
  const activeContactModel = useStore((state) => state.activeContactModel);
  const activeContact = useStore((state) => state.activeContact);
  const toggleContactStar = useStore((state) => state.toggleContactStar);
  const toggleContactArchive = useStore((state) => state.toggleContactArchive);
  const downloadContactPDF = useStore((state) => state.downloadContactPDF);
  const canTranslate = useFeature('ai-translation');

  const [pdfLoading, setPdfLoading] = useState(false);

  const handleDownloadContactPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await downloadContactPDF(activeContactModel.id);
      const link = document.createElement("a");
      link.href = `${res.data.url}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.log(error);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleContactMenuClick = ({ key }) => {
    if (key === "1") {
      Modal.confirm({
        title: "Confirm Delete",
        content: `Are you sure you want to delete ${activeContactModel.name}?`,
        onOk: () => onDeleteContact(activeContactModel.id),
      });
    }
  };

  if (!activeContact || !activeContactModel || !activeContactModel.id) {
    return (
      <Flex vertical style={{ padding: "8px 0", gap: 0, lineHeight: "normal" }}>
        {/* Row 1: Name + Action Buttons */}
        <Row justify="space-between" align="middle" wrap={false}>
          <Col style={{ minWidth: 0, flex: 1 }}>
            <Skeleton.Input size="small" style={{ width: 160, height: 28 }} />
          </Col>
          <Col>
            <Space size={4}>
              <Skeleton.Button size="small" style={{ width: 32, minWidth: 32 }} />
              <Skeleton.Button size="small" style={{ width: 32, minWidth: 32 }} />
              <Skeleton.Button size="small" style={{ width: 32, minWidth: 32 }} />
              <Skeleton.Button size="small" style={{ width: 32, minWidth: 32 }} />
            </Space>
          </Col>
        </Row>
        {/* Row 2: Phone + Tags */}
        <Flex align="center" gap={8} style={{ marginTop: 4 }}>
          <Skeleton.Input size="small" style={{ width: 120, height: 18 }} />
          <Space size={4}>
            <Skeleton.Button size="small" style={{ width: 50, minWidth: 50, height: 22, borderRadius: 4 }} />
            <Skeleton.Button size="small" style={{ width: 64, minWidth: 64, height: 22, borderRadius: 4 }} />
          </Space>
        </Flex>
        {/* Row 3: Groups */}
        <div style={{ marginTop: 8 }}>
          <Space size={4}>
            <Skeleton.Button size="small" style={{ width: 56, minWidth: 56, height: 22, borderRadius: 12 }} />
            <Skeleton.Button size="small" style={{ width: 72, minWidth: 72, height: 22, borderRadius: 12 }} />
          </Space>
        </div>
      </Flex>
    );
  }

  return (
    <Flex vertical style={{ padding: "8px 0", gap: 0, lineHeight: "normal" }}>
      {/* Row 1: Name + Action Buttons */}
      <Row justify="space-between" align="middle" wrap={false}>
        <Col style={{ minWidth: 0, flex: 1 }}>
          <Title
            level={4}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              margin: 0,
              marginBottom: 0,
              padding: 0,
              lineHeight: 1.3,
              cursor: "pointer",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            onClick={() =>
              history.push(`/contacts/${activeContactModel.id}`)
            }
          >
            {activeContactModel.blacklist && (
              <Tooltip title="Opted out" placement="top">
                <StopOutlined
                  style={{ color: "#ff4d4f", fontSize: 12, flexShrink: 0 }}
                />
              </Tooltip>
            )}
            {activeContactModel.name}
          </Title>
        </Col>
        <Col>
          <Space size={4}>
            <Tooltip
              title={
                activeContactModel.star ? "Remove Star" : "Star"
              }
              placement="bottom"
            >
              <Button
                onClick={toggleContactStar}
                style={{
                  color: activeContactModel.star ? "gold" : "gray",
                }}
                icon={<StarFilled />}
              />
            </Tooltip>
            <Tooltip
              title={activeContactModel.archived ? "Move back to Inbox" : "Archive"}
              placement="bottom"
            >
              <Button
                className="gh-archive-btn"
                onClick={toggleContactArchive}
                style={{
                  color: activeContactModel.archived ? "#1677ff" : "gray",
                }}
                icon={<ContainerOutlined />}
              />
            </Tooltip>
            <Tooltip title="Download PDF" placement="bottom">
              <Button
                style={{ color: "gray" }}
                icon={<FilePdfFilled />}
                loading={pdfLoading}
                onClick={handleDownloadContactPDF}
              />
            </Tooltip>
            <Dropdown
              menu={{ items: menuItems, onClick: handleContactMenuClick }}
              placement="bottomLeft"
            >
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          </Space>
        </Col>
      </Row>

      {/* Row 2: Phone + Tags */}
      <Flex align="center" gap={8} wrap style={{ marginTop: 4, verticalAlign:'center' }}>
        {isWebchatMobile(activeContactModel.mobile) ? (
          <>
            <Tooltip title="Webchat">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#52c41a', fontSize: 13 }}>
                <GlobalOutlined /> Webchat
              </span>
            </Tooltip>
            {activeContactModel.contactdetails?.find((d: any) => d.field === 'mobile')?.value && (
              <PhoneDisplay value={activeContactModel.contactdetails.find((d: any) => d.field === 'mobile').value} />
            )}
          </>
        ) : (
          <ContactChannelChips contact={activeContactModel} />
        )}
        {canTranslate && <ContactLanguagePill contact={activeContactModel} />}
        <TagsComponent />
      </Flex>

      {/* Row 3: Groups */}
      <div style={{marginTop:'8px'}}>
        <GroupsComponent />
      </div>
    </Flex>
  );
};

export default withRouter(ActiveContactHeader);
